// KeyboardAdapter — Default implementation for keyboard shortcuts
// Injected into VNInvestCharts to avoid direct window.addEventListener
// All global access is lazy-initialized inside the factory function for tree-shaking
export const createDefaultKeyboardAdapter = () => {
    const handlers = new Map();
    const keydownHandlers = new Set();
    const processedEvents = new WeakSet();
    // Lazy-initialized event handler attachment
    let isAttached = false;
    const attach = () => {
        if (isAttached || typeof window === 'undefined')
            return;
        const handleKeyboardEvent = (e) => {
            if (processedEvents.has(e))
                return;
            processedEvents.add(e);
            keydownHandlers.forEach(handler => handler(e));
            if (e.type === 'keydown') {
                // Check for shortcut combos
                const combo = getComboString(e);
                const comboHandlers = handlers.get(combo);
                if (comboHandlers) {
                    e.preventDefault();
                    comboHandlers.forEach(handler => handler());
                }
            }
        };
        // Attach to both window and document for full testing/JSDOM/browser coverage
        window.addEventListener('keydown', handleKeyboardEvent);
        window.addEventListener('keyup', handleKeyboardEvent);
        if (typeof document !== 'undefined') {
            document.addEventListener('keydown', handleKeyboardEvent);
            document.addEventListener('keyup', handleKeyboardEvent);
        }
        isAttached = true;
    };
    // Ensure attachment on first use
    const ensureAttached = () => {
        if (!isAttached)
            attach();
    };
    return {
        onKeyDown: (handler) => {
            ensureAttached();
            keydownHandlers.add(handler);
            return () => keydownHandlers.delete(handler);
        },
        onShortcut: (combo, handler) => {
            ensureAttached();
            const normalized = normalizeCombo(combo);
            if (!handlers.has(normalized)) {
                handlers.set(normalized, new Set());
            }
            handlers.get(normalized).add(handler);
            return () => {
                handlers.get(normalized)?.delete(handler);
                if (handlers.get(normalized)?.size === 0) {
                    handlers.delete(normalized);
                }
            };
        },
    };
};
// Singleton - lazy initialized on first access
let _defaultKeyboardAdapter = null;
export const getDefaultKeyboardAdapter = () => {
    if (!_defaultKeyboardAdapter) {
        _defaultKeyboardAdapter = createDefaultKeyboardAdapter();
    }
    return _defaultKeyboardAdapter;
};
// Backward compatibility - lazy getter
export const defaultKeyboardAdapter = new Proxy({}, {
    get(target, prop) {
        const adapter = getDefaultKeyboardAdapter();
        return adapter[prop];
    }
});
// ============================================
// Combo String Helpers
// ============================================
export function getComboString(e) {
    const parts = [];
    if (e.ctrlKey || e.metaKey)
        parts.push('ctrl');
    if (e.shiftKey)
        parts.push('shift');
    if (e.altKey)
        parts.push('alt');
    if (e.key.length === 1) {
        parts.push(e.key.toLowerCase());
    }
    else {
        parts.push(e.key.toLowerCase());
    }
    return parts.join('+');
}
export function normalizeCombo(combo) {
    return combo
        .toLowerCase()
        .split('+')
        .map(part => part.trim())
        .sort((a, b) => {
        // Order: ctrl, shift, alt, key
        const order = { ctrl: 0, shift: 1, alt: 2 };
        const oa = order[a] ?? 3;
        const ob = order[b] ?? 3;
        return oa - ob;
    })
        .join('+');
}
// ============================================
// Predefined Shortcut Constants — REMOVED 2026-09-30
// ============================================
//
// `export const SHORTCUTS` + `export type ShortcutKey` are GONE. They had 0 consumers
// outside their own test, and were a second, WRONG copy of the bindings: TOOL_CROSSHAIR
// 'c' vs the widget's `C: closePosition` / `Shift+C`; TOGGLE_SYNC_CROSSHAIR 'shift+c'
// vs `Control+Shift+KeyH`; TOOL_MEASURE 'm' vs `M: toggleMagnet`; TOOL_TRENDLINE 't'
// vs `Alt+T`; and SAVE_WORKSPACE 'ctrl+s' collides with the browser's own save.
//
// Canonical source: widget src/widget/config/toolShortcuts.ts +
// widget src/widget/hooks/ShortcutManager.tsx. See CHANGELOG.md (2.0.2).
//
// NOTE: the rest of this file is NOT dead — createDefaultKeyboardAdapter,
// getDefaultKeyboardAdapter, defaultKeyboardAdapter, getComboString and normalizeCombo
// are all still exported and used.
// ============================================
